import {
  fetchApiRef,
  identityApiRef,
  useApi,
} from '@backstage/core-plugin-api';
import {
  fetchData,
  urls,
} from '@internal/backstage-plugin-dh-dock-shared-common';
import { isArray } from 'lodash';
import { useCallback, useEffect, useState } from 'react';
import React from 'react';

import failureIcon from '../../app/src/assets/icons/failed.png';
import successIcon from '../../app/src/assets/icons/success.png';
import Modal from './components/Modal';

import 'react-toastify/dist/ReactToastify.css';
import { dhDockStore } from './redux/store';
import useCustomEntities from './hooks/useEntities';
import { Entity } from '@backstage/catalog-model';


enum pipelineStatus {
  succeeded = 'succeeded',
  failed = 'failed',
}

interface pipleLineResponse {
  data: any;
  body: string;
}
function hideArticle() {
  const articles = document.getElementsByTagName('article'); // Get all <article> elements
  if (articles.length > 0) {
    articles[0].replaceWith(); // Remove the first <article> element found
  }
}
function isAppWithNumber(term: string) {
  if (typeof term !== 'string') return false;

  const lower = term.toLowerCase();

  if (!lower.startsWith('app-')) return false;

  const numberPart = lower.slice(4);

  return numberPart !== '' && !Number.isNaN(Number(numberPart));
}

export const GlobalApiInterceptor = () => {
  const fetchApi = useApi(fetchApiRef);
  const [showModal, setShowModal] = useState(false);
  const [responseData, setResponseData] = useState<pipleLineResponse>({
    body: '',
    data: {},
  });
  const [taskId, setTaskId] = useState('');
  const [trackSpace_Id, setTrackSpace_Id] = useState('');
  const [trackSpaceTicket, setTrackSpaceTicket] = useState('');
  const identityApi = useApi(identityApiRef);
  const { getCustomEntitiesByKind: productList } = useCustomEntities('System');

  const getPipeLineStatus = useCallback(async () => {
    try {
      const status = await fetchData(
        `${urls.getPiplineStatusByTasKId}${taskId}`,
      );
      if (status) {
        return status;
      }
    } catch (error) {
      console.error('Error fetching pipeline status:', error);
    }
  }, [taskId]);

  useEffect(() => {
    let intervalId: any = 0;
    if (window.location.pathname.split('/')[2] === 'tasks') {
      const id = window.location.pathname.split('/')[3];
      const isValidId = /^[a-zA-Z0-9]+$/.test(id);
      if (!isValidId) {
        throw new Error('Invalid ID format');
      }
      const getStatus = async () => {
        const status = await fetchData(`${urls.getPiplineStatusByTasKId}${id}`);
        if (status) {
          if (
            status.data &&
            (status.data.result === pipelineStatus.succeeded ||
              status.data.result === pipelineStatus.failed)
          ) {
            setShowModal(true);
            setResponseData(status);
            clearInterval(intervalId); // Clear the interval if you get the desired response
          }
        }
        if (taskId) {
          intervalId = setInterval(() => {
            getStatus(); // Call the async function inside the interval
          }, 5000); // Run every 5 seconds
        }

        // Cleanup on taskId change or component unmount
        return () => clearInterval(intervalId);
      };
      getStatus();
    }
  }, []);
  // # region Region code is for pipeline response
  useEffect(() => {
    let intervalId: any = 0;

    const checkStatus = async () => {
      const response = (await getPipeLineStatus()) as pipleLineResponse; // Call the async function
      if (response) {
        if (
          response.data &&
          (response.data.result === pipelineStatus.succeeded ||
            response.data.result === pipelineStatus.failed)
        ) {
          setShowModal(true);
          setResponseData(response);
          clearInterval(intervalId); // Clear the interval if you get the desired response
        }
      }
    };

    if (taskId) {
      intervalId = setInterval(() => {
        checkStatus(); // Call the async function inside the interval
      }, 5000); // Run every 5 seconds
    }

    // Cleanup on taskId change or component unmount
    return () => clearInterval(intervalId);
  }, [taskId, getPipeLineStatus]);
  //#endregion

  // # region Region code is for trackspace response
  useEffect(() => {
    let intervalId: any = 0;

    const checkTrackSpaceStatus = async () => {
      const trackSpaceId = await fetchData(
        `${urls.getCreatedTrackSpaceId}${trackSpace_Id}`,
      );
      if (trackSpaceId?.length && isArray(trackSpaceId)) {
        const id = trackSpaceId[0]?.body
          ?.split('ID:')[1]
          ?.split('"')[0]
          .replace(' ', '');
        setTrackSpaceTicket(id);
        clearInterval(intervalId); // Clear the interval if you get the desired response
      }
    };

    if (trackSpace_Id) {
      intervalId = setInterval(() => {
        checkTrackSpaceStatus(); // Call the async function inside the interval
      }, 5000); // Run every 5 seconds
    }

    // Cleanup on taskId change or component unmount
    return () => clearInterval(intervalId);
  }, [trackSpace_Id]);
  //#endregion
  useEffect(() => {
    if (window.location.pathname.includes('tasks')) {
      hideArticle();
    }
  }, [window.location]);

  const handleJoinWorkingGroup = async (
    input: string,
    loggedInUser: string,
  ) => {
    const data = input?.toString()?.split('/');
    const joinProject = (await fetchData(urls.joinProject, {
      projectId: data?.[6],
      user: loggedInUser as string,
    })) as Response;
    return joinProject;
  };

  const handleLeaveProject = async (input: string, loggedInUser: string) => {
    const data = input?.toString()?.split('/');
    const leaveProject = (await fetchData(urls.leaveBazaarProject, {
      projectId: data?.[6],
      user: loggedInUser as string,
    })) as Response;
    return leaveProject;
  };

   const handleSearch = (input: string, docName: string | null) => {
    if (!docName) {
      return input;
    }

    const url = new URL(input, window.location.origin);

    // Example: update filters[kind] from group → component
    if (url.searchParams.get('filters[kind]') === 'group') {
      url.searchParams.set('filters[kind]', 'docs');
    }

    url.searchParams.set('filters[name]', docName.toLowerCase());
    // Reassign modified URL back to input
    return url.toString();
  };

  const handleBazaarProjectResponse = async (response: Response) => {
    const cloned = response.clone();
    const body = await cloned.json();

    if (Array.isArray(body.data)) {
      // Deduplicate by user_ref
      const arr = body.data.filter(
        (item: { user_ref: any }, index: any, self: any[]) =>
          index === self.findIndex(i => i.user_ref === item.user_ref),
      );

      // 🔹 Sanitize user_id for each item
      const sanitized = arr.map((item: { user_id: string }) => ({
        ...item,
        user_id: item.user_id
          ? item.user_id.replaceAll('%2C%20', ', ')
          : item.user_id,
      }));

      body.data = sanitized;
    }

    return new Response(JSON.stringify(body), {
      status: response.status,
      statusText: response.statusText,
      headers: { 'Content-Type': 'application/json' },
    });
  };

  const handleTrackSpace = async (response: Response) => {
    if (response.ok) {
      const body = response.clone().json();
      const { id } = await body;
      setTrackSpace_Id(id);
    }
  };
  const handleTaskId = async (response: Response) => {
    if (response.ok) {
      const body = response.clone().json();
      const { id } = await body;
      setTaskId(id);
    }
  };

  const handleSearchByAppId = async (response: Response, term?: string) => {
    // load all products
    
    const products = (await productList()).items;
    if (isAppWithNumber(term?.toLowerCase()!)) {
      const data = products.filter(x =>
        x.metadata.annotations?.['digitalhangar.aero/leanix-appid']
          ?.toLowerCase()
          ?.includes(term!?.toLowerCase()),
      );
      const cloned = response.clone();
      const body = await cloned.json();

      if (Array.isArray(body.results)) {
        body.results = data.map((item: Entity, index: number) => {
          const appId=item.metadata.annotations?.['digitalhangar.aero/leanix-appid']
         
          const spec = item.spec as any;
          return {
            ...item,
            type: 'software-catalog',
            rank: index + 1,
            document: {
              kind: 'System',
              text: item.metadata.description?.concat(
                `. ${appId}`,
              ),
              type: spec?.type,
              owner: spec?.owner,
              title: item.metadata.title?.concat(` ${appId}`),
              location: `/catalog/default/system/${item.metadata.name}`,
              Lifecycle: spec.lifecycle,
              namespace: 'default',
            },
            highlight: {
              fields: {
               // title:appId?.toLowerCase().trim()?.includes(term?.toLowerCase()!)?item.metadata.title:'',
                location:`/catalog/default/system/${item.metadata.name}`,
                text: item.metadata.description?.concat(
                `. ${appId}`,
              ),
              },
            },
          };
        });

        return new Response(JSON.stringify(body), {
          status: response.status,
          statusText: response.statusText,
          headers: { 'Content-Type': 'application/json' },
        });
      }
    }
    return response;
  };

  const handleProjectList = async (response: Response) => {
    const cloned = response.clone();
    const body = await cloned.json();
    const store = dhDockStore.getState();
    const { projectMembersList } = store.globalState as any;
    body.data = body?.data?.map((_item: any) => {
      const membersCount = projectMembersList?.find(
        (x: { id: any }) => x.id === _item?.id,
      )?.memberscount;
      return {
        ..._item,
        members_count: membersCount ?? 0,
      };
    });
    return new Response(JSON.stringify(body), {
      status: response.status,
      statusText: response.statusText,
      headers: { 'Content-Type': 'application/json' },
    });
  };

  const showAllWorkingGroupMembers = async (response: Response) => {
    const cloned = response.clone();
    const body = await cloned.json();

    const headings = document.querySelectorAll('h2');
    const heading = Array.from(headings).find(
      el => el?.innerText!!.trim() === ('LATEST MEMBERS' as any),
    );

    if (body?.data[0]?.user_ref && body?.data?.length > 7) {
      const result = body?.data?.slice(0, body?.data?.length - 7);

      if (heading && !document.querySelector('#all-members')) {
        const newDiv = document.createElement('div');
        newDiv.id = 'all-members';
        newDiv.style.marginTop = '5px';
        newDiv.style.marginBottom = '10px';
        newDiv.style.display = 'flex';
        newDiv.style.flexDirection = 'column';
        newDiv.style.gap = '8px';
        newDiv.style.color = '#05164d';

        // Build each user element
        result.forEach((result: any) => {
          const userDiv = document.createElement('div');
          userDiv.style.display = 'block';
          userDiv.style.alignItems = 'center';
          userDiv.style.gap = '8px';

          const img = document.createElement('img');
          if (!result.picture) {
            img.style.background = '#ccc';
          } else {
            img.src = result.picture;
          }
          img.style.width = '20px';
          img.style.height = '20px';
          img.style.borderRadius = '50%';
          img.style.objectFit = 'cover';

          const name = document.createElement('a');
          name.target = '_blank';
          name.rel = 'noopener noreferrer';
          name.href = `/catalog/default/user/${result?.user_ref?.split('user:default/')[1]}`;
          name.textContent = result.user_id;
          name.style.marginLeft = '4px';

          userDiv.appendChild(img);
          userDiv.appendChild(name);
          newDiv.appendChild(userDiv);
        });

        // Insert the list under the heading
        heading.insertAdjacentElement('afterend', newDiv);
      }
    }

    return response;
  };

  useEffect(() => {
    const originalFetch = fetchApi.fetch.bind(fetchApi);
    let loggedInUser: string | undefined;
    identityApi?.getProfileInfo().then(userId => {
      loggedInUser = userId.email;
    });
    // Override the fetch function with our custom error handler
    fetchApi.fetch = async (input, init) => {
      if (input?.toString()?.includes('?term=')) {
        input = decodeURIComponent(input.toString());

        input = input.replace(/%20/g, ' ');

        input = encodeURI(input);
      }

      try {
        const _body = init?.body as any;
        // to join working group
        if (
          init?.method === 'PUT' &&
          input?.toString()?.includes('/api/bazaar/projects/')
        ) {
          return handleJoinWorkingGroup(
            input as string,
            loggedInUser as string,
          );
        }

        // to leave working group
        if (
          init?.method === 'DELETE' &&
          input?.toString()?.includes('/api/bazaar/projects/')
        ) {
          return handleLeaveProject(input as string, loggedInUser as string);
        }
        if (
          input?.toString()?.includes('/api/search/query?') &&
          window.location.pathname?.includes('group')
        ) {
          const store = dhDockStore.getState();
          const { selectedGroup } = store.globalState as any;
          input = handleSearch(input as string, selectedGroup as string);
        }

        const response = await originalFetch(input, init);

        // GET working group projects

        if (input?.toString()?.includes('/api/bazaar/projects?')) {
          return handleProjectList(response);
        }
        if (input?.toString()?.includes('/api/bazaar/projects/')) {
          // inject an html element into the DOM to show the remaining
          return showAllWorkingGroupMembers(response);
        }

        if (response.ok && response.url?.includes('/api/search/query?term=')) {
          
          const params=new URL(input?.toString()).searchParams
          const term =params.get("term")
          return handleSearchByAppId(response, term!);
        }

        if (
          _body &&
          JSON.parse(_body)?.templateRef?.split('/')[1] ===
            'create-trackspace-ticket'
        ) {
          handleTrackSpace(response);
        }

        if (
          _body &&
          JSON.parse(_body)?.templateRef?.split('/')[1]?.includes('template')
        ) {
          handleTaskId(response);
        }
        if (input?.toString()?.includes('/api/bazaar/projects/')) {
          return handleBazaarProjectResponse(response);
        }

        return response;
      } catch (error) {
        console.error('API request failed:', error);
        return new Response(JSON.stringify({ error: 'Request failed' }), {
          status: 500,
          headers: { 'Content-Type': 'application/json' },
        });
      }
    };

    // Cleanup on unmount
    return () => {
      fetchApi.fetch = originalFetch;
    };
  }, [fetchApi]);

  const closeModal = () => {
    setShowModal(false);
    window.location.href = '/create';
  };

  if (trackSpaceTicket) {
    hideArticle();
    return (
      <>
        <Modal onClose={closeModal}>
          <img src={successIcon} alt="success" />
          <div>
            <h2>{`Trackspace ticket created successfully with ID ${trackSpaceTicket}. `}</h2>

            <p>
              Click &nbsp;
              <a
                href={`https://trackspace.lhsystems.com/browse/${trackSpaceTicket}`}
                target="_blank"
                rel="noopener noreferrer"
                style={{ color: 'blue' }}
              >
                here &nbsp;
              </a>
              to view the ticket
            </p>
          </div>
        </Modal>
        ;
      </>
    );
  }

  if (showModal) {
    hideArticle();
  }

  return showModal && responseData ? (
    <Modal onClose={closeModal}>
      {responseData?.data?.result === pipelineStatus.succeeded && (
        <img src={successIcon} alt="success" />
      )}
      {responseData?.data?.result === pipelineStatus.failed && (
        <img src={failureIcon} alt="failure" />
      )}
      <h2>
        {responseData?.data?.result === pipelineStatus.succeeded
          ? 'Your template execution was successful'
          : 'Your template execution failed'}
      </h2>
      {responseData?.data?.result === pipelineStatus.failed && (
        <p>
          Click &nbsp;
          <a
            href={responseData?.body ? encodeURI(responseData.body) : '#'}
            target="_blank"
            rel="noopener noreferrer"
            style={{ color: 'blue' }}
          >
            here &nbsp;
          </a>
          to view pipeline
        </p>
      )}

      {responseData?.data?.result === pipelineStatus.succeeded && (
        <p>
          Click &nbsp;
          <a
            href={
              responseData?.data?.templateParameters?.adoProjectName
                ? encodeURI(
                    `https://dev.azure.com/${responseData?.data?.templateParameters?.organization}/${responseData?.data?.templateParameters?.adoProjectName}/_git/${responseData?.data?.templateParameters?.adoRepositoryName}`,
                  )
                : encodeURI(
                    `https://dev.azure.com/${responseData?.data?.templateParameters?.organization}/${responseData?.data?.templateParameters?.productName}`,
                  )
            }
            target="_blank"
            rel="noopener noreferrer"
            style={{ color: 'blue' }}
          >
            here &nbsp;
          </a>
          to view your repository
        </p>
      )}
    </Modal>
  ) : null; // This component doesn't render anything
};
